import { type ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import { Icons, Input } from '@/js/libs/ui/src/V2';

type SearchFilterProps = {
  disabled?: boolean;
  intOnly?: boolean;
  filter?: string;
  placeholder?: string;
  fullWidth?: boolean;
  dataTestId?: string;
  isLoading?: boolean;
  onChange: (value: string) => void;
};

export const SearchFilter = memo(function SearchFilter({
  disabled,
  intOnly,
  filter,
  onChange,
  placeholder,
  fullWidth,
  dataTestId = 'search-field',
  isLoading = false,
}: SearchFilterProps) {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();

  const isMobile = useMediaQuery({ query: Media.xsMax });

  const isChangeAllowed = useCallbackRef((e: ChangeEvent<HTMLInputElement>): boolean => {
    const { value } = e.currentTarget;
    if (intOnly && value.length > 0) {
      return /^\d+$/.test(value);
    }
    return true;
  });

  const onFilterChangeChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    if (isChangeAllowed(e)) {
      onChange(e.currentTarget.value);
    }
  });

  const onClear = useCallbackRef(() => {
    onChange('');
  });

  return (
    <Input
      disabled={disabled}
      width={fullWidth || isMobile ? '100%' : '220px'}
      placeholder={placeholder ?? t('common:PLACEHOLDER_SEARCH')}
      value={filter}
      onChange={onFilterChangeChange}
      icon={
        filter ? (
          <Icons.Clear filled size={20} color={colors.grey100_1} onClick={onClear} data-testid='icons-clear' />
        ) : (
          <Icons.Search size={20} color={colors.grey100_1} data-testid='icons-search' />
        )
      }
      isActionIcon={!!filter}
      dataTestId={dataTestId}
      isLoading={isLoading}
    />
  );
});
